import {
    CustomProtocolService,
    Evaluation,
    EvaluationService,
    FilterBuilder,
    OrganizationProtocolPreference,
    ProtocolFilter,
    Sample,
    SampleService,
    Status,
    translateCommonErrorStatus
} from '@core/data';
import {Library, Protocol, SampleType, Schedule} from '@library';
import {Action, State, StateContext, Selector} from '@ngxs/store';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import * as moment from 'moment';
import {LIBRARY} from '@app/evaluation/library';
import {Inject, Injectable} from '@angular/core';
import { OrganizationProtocolPreferencesService } from '@core/data/api/organization-protocol-preferences.service';
import { Snackbar } from '@core/material';
import { EvaluationFormDefaults } from './evaluation-form.dialog';


export interface EvaluationFormStateModel {
    key: string; // evaluation key
    status: Status;
    data: Partial<Evaluation>;
    bulkKeys: string[];
    cropId: string,
    cropIds: string[],
    sampleType: SampleType,
    sample: Sample;
    protocolOptions: Protocol[];
    scheduleOptions: Schedule[];
    orgKey: string;
    preferences: OrganizationProtocolPreference[];
}

const DEFAULTS: EvaluationFormStateModel = {
    key: null,
    status: Status.UNINITIALIZED,
    data: null,
    bulkKeys: [],
    cropId: null,
    cropIds: [],
    sampleType: null,
    sample: null,
    protocolOptions: [],
    scheduleOptions: [],
    orgKey: null,
    preferences: [],
};

export class InitEvaluationForm {
    static readonly type = "[EvaluationForm] Init";

    constructor(
        public orgKey: string,
        public key?: string,
        public defaults?: EvaluationFormDefaults
    ) {
    }
}

export class InitBulkEvaluationForm {
    static readonly type = "[EvaluationForm] Init Bulk";
    constructor(public sampleKeys: string[], public orgKey: string, public cropId: string, public sampleType: SampleType, public cropIds: string[] = []){}
}

export class SubmitEvaluationForm {
    static readonly type = "[EvaluationForm] Submit";

    constructor(public data: Partial<Evaluation>) {
    }
}

export class SubmitBulkEvaluationForm {
    static readonly type = "[EvaluationForm] Submit Bulk";
    constructor(public data: Partial<Evaluation>[]) {
    }
}

export class LoadOrganisationPreference {
    static readonly type = '[EvaluationForm] Load Preference';
    constructor(public orgKey: string) {}
}

@State({
    name: "evaluation_form",
    defaults: DEFAULTS
})
@Injectable()
export class EvaluationFormState {

    @Selector()
    static data(state: EvaluationFormStateModel) {
        return state.data;
    }

    constructor(
        private _snackbar: Snackbar,
        private _evalService: EvaluationService,
        private _sampleService: SampleService,
        private _custom_protocolService: CustomProtocolService,
        private _protocol_preferencesService: OrganizationProtocolPreferencesService,
        @Inject(LIBRARY) private _library: Library
    ) {
    }

    @Action(InitEvaluationForm)
    initEvaluationForm(ctx: StateContext<EvaluationFormStateModel>, action: InitEvaluationForm) {
        ctx.setState(DEFAULTS);

        if (action.key) return this.initEditForm(ctx, action.orgKey, action.key, action.defaults);
        return this.initNewForm(ctx, action.orgKey, action.defaults);
    }

    @Action(InitBulkEvaluationForm)
    initBulkEvaluationForm(ctx: StateContext<EvaluationFormStateModel>, action: InitBulkEvaluationForm) {
        ctx.setState(DEFAULTS);

        const evalu: Partial<Evaluation> = {
            size: 1,
            evalStartDate: moment(),
        }

        ctx.patchState({
            orgKey: action.orgKey,
            bulkKeys: action.sampleKeys,
            cropId: action.cropId,
            cropIds: action.cropIds,
            sampleType: action.sampleType,
            status: Status.LOADING,
            data: evalu,
        })

        return ctx.dispatch(new LoadOrganisationPreference(action.orgKey));
    }

    @Action(SubmitEvaluationForm)
    submitEvaluationForm(ctx: StateContext<EvaluationFormStateModel>, action: SubmitEvaluationForm) {

        const state = ctx.getState();

        ctx.patchState({
            status: Status.LOADING
        });

        let request: Observable<Evaluation>;

        if (state.key) {
            request = this._evalService.update(state.key, action.data);
        } else {
            request = this._evalService.create(action.data);
        }

        return request
            .pipe(tap(
                result => {
                    ctx.patchState({
                        status: Status.COMPLETE,
                        data: result,
                    });
                },
                error => {
                    ctx.patchState({status: translateCommonErrorStatus(error)});
                }
            ));
    }

    @Action(SubmitBulkEvaluationForm)
    submitBulkEvaluationForm(ctx: StateContext<EvaluationFormStateModel>, action: SubmitBulkEvaluationForm) {
        ctx.patchState({
            status: Status.LOADING
        });

        return this._evalService.createMany({ evals: action.data} )
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            status: Status.COMPLETE,
                        });
                    },
                    error => {
                        ctx.patchState({status: translateCommonErrorStatus(error)});
                    }
                )
            );
    }

    @Action(LoadOrganisationPreference)
    loadOrganisationPreference(ctx: StateContext<EvaluationFormStateModel>, action: LoadOrganisationPreference) {
        const state = ctx.getState();

        ctx.patchState({
            status: Status.LOADING,
        })

        const filter = (new FilterBuilder());

        filter.setQuery('ownerOrgKey', action.orgKey);

        return this._protocol_preferencesService.query(filter.get())
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            preferences: result.data,
                        })

                        this.loadProtocolOptions(ctx, state.orgKey, result.data);
                    },
                    error => {
                        ctx.patchState({
                            preferences: state.preferences ? state.preferences : [],
                            status: translateCommonErrorStatus(error)
                        });
                    }
                )
            )
    }

    private initNewForm(ctx: StateContext<EvaluationFormStateModel>, orgKey: string, defaults: Partial<Evaluation>) {

        if (!defaults?.sampleKey) {
            console.error("EvaluationFormState: No sampleKey provided");
            ctx.patchState({
                orgKey: orgKey,
                status: Status.ERROR,
            });
            return;
        }

        ctx.patchState({
            orgKey: orgKey,
            status: Status.LOADING,
        });

        return this._sampleService
            .get(defaults.sampleKey, {related: ['scionCultivar']})
            .pipe(
                tap(
                    result => {

                        ctx.patchState({
                            data: {
                                size: result.size || 1,
                                evalStartDate: moment(),
                                ...defaults,
                            },
                            sample: result,
                            scheduleOptions: this.getScheduleOptions(result),
                            sampleType: result.type,
                            cropId: result.scionCultivar.cropId,
                        });

                        ctx.dispatch(new LoadOrganisationPreference(orgKey));
                    },
                    error => {
                        ctx.patchState({status: translateCommonErrorStatus(error)});
                    }
                )
            );

    }

    private initEditForm(ctx: StateContext<EvaluationFormStateModel>, orgKey: string, key: string, defaults: Partial<Evaluation>) {

        ctx.patchState({
            orgKey,
            key,
            status: Status.LOADING
        });

        return this._evalService
            .get(key, {related: ['sample', 'sample.scionCultivar']})
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            orgKey: orgKey,
                            data: result,
                            sample: result.sample,
                            scheduleOptions: this.getScheduleOptions(result.sample),
                            sampleType: result.sample.type,
                            cropId: result.sample.scionCultivar.cropId,
                        });

                        ctx.dispatch(new LoadOrganisationPreference(orgKey));
                    },
                    error => {
                        ctx.patchState({status: translateCommonErrorStatus(error)});
                    },
                )
            );

    }

    private loadProtocolOptions(ctx: StateContext<EvaluationFormStateModel>, orgKey: string, preferences: OrganizationProtocolPreference[]) {
        const state = ctx.getState();

        const protocolId = state.data?.protocolId || null;
        const crop = state.cropIds && state.cropIds.length > 0
            ? state.cropIds
            : (state.cropId ? [state.cropId] : []);
        const type = state.sampleType ? [state.sampleType] : []

        const filter: Partial<ProtocolFilter> = {
            showFavourites: true,
            showHidden: false,
            crops: crop,
            sampleTypes: type,
            currentId: protocolId,
        }

        this._custom_protocolService.allProtocolsObserved(orgKey, preferences, filter).subscribe(all => {

            // allProtocolsObserved()'s crop filter is a union across crops — for a
            // multi-crop bulk selection this needs the true intersection, so narrow
            // the result down to protocols common to every selected crop.
            if (state.cropIds && state.cropIds.length > 1) {
                const { commonProtocolIds } = this._library.isSelectionCompatible(state.cropIds);
                const commonProtocolIdSet = new Set(commonProtocolIds);
                all = all.filter(protocol => commonProtocolIdSet.has(protocol.id));
            }

            if (!all || all.length == 0) this._snackbar.error("No protocols found, contact your Organisation's Administrators to update your protocol preferences.")

            ctx.patchState({
                status: Status.OK,
                protocolOptions: all,
            })
        });
    }

    private getScheduleOptions(sample: Sample) {

        if (sample.scionCultivar) {
            return this._library.schedulesByCrop(sample.scionCultivar.cropId);
        }

        return this._library.schedules.all();

    }


}
